import type { Twin as TwinState } from '@trendator/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import { ChevronDim, CloseIcon, DuoIcon, LockIcon, MicIcon, Plus, TickIcon } from '../components/icons';
import { haptic, hapticResult } from '../telegram';
import { Loading } from './Status';

/** Фото двойника — по одной картинке, через fetch с авторизацией (кэш на время жизни экрана). */
function TwinThumb({ id, ok, onRemove }: { id: string; ok: boolean; onRemove: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    if (ok) {
      api
        .twinPhotoUrl(id)
        .then((u) => {
          made = u;
          if (alive) setUrl(u);
          else URL.revokeObjectURL(u);
        })
        .catch(() => {});
    }
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [id, ok]);
  return (
    <span className={`twin-thumb${ok ? '' : ' twin-thumb--bad'}`}>
      {url ? <img src={url} alt="" /> : null}
      <button type="button" className="twin-thumb__x" aria-label="Убрать фото" onClick={onRemove}>
        <CloseIcon size={12} />
      </button>
    </span>
  );
}

/** Двойник — docs/design/Twin.dc.html. */
export function Twin({ onLiveness }: { onLiveness: () => void }) {
  const [twin, setTwin] = useState<TwinState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .twin()
      .then(setTwin)
      .catch(() => setError('Не получилось загрузить двойника. Проверь интернет.'));
  }, []);

  if (!twin) return error ? <p className="error-text twin__msg">{error}</p> : <Loading inline />;

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const list = [...files].slice(0, Math.max(1, twin.maxPhotos - twin.okPhotos));
    setUploading({ done: 0, total: list.length });
    let last = twin;
    for (const [i, f] of list.entries()) {
      try {
        last = await api.twinUpload(f);
        setTwin(last);
      } catch (err) {
        const code = err instanceof ApiError ? err.code : '';
        setError(
          code === 'too_many_photos'
            ? `Максимум ${twin.maxPhotos} фото`
            : code === 'twin_disabled'
              ? 'Двойник ещё подключается — загляни чуть позже.'
              : code === 'too_many_uploads'
                ? 'Слишком много фото за час — продолжи позже.'
                : 'Не все фото загрузились — попробуй ещё раз.',
        );
        if (code === 'too_many_photos' || code === 'twin_disabled' || code === 'too_many_uploads') break;
      }
      setUploading({ done: i + 1, total: list.length });
    }
    setUploading(null);
    if (fileRef.current) fileRef.current.value = '';
    if (last.status === 'liveness' && twin.status === 'photos') hapticResult('success');
  };

  const remove = async (id: string) => {
    haptic();
    try {
      setTwin(await api.twinDeletePhoto(id));
    } catch {
      setError('Не получилось убрать фото.');
    }
  };

  const del = async () => {
    try {
      setTwin(await api.deleteTwin());
      setConfirmDelete(false);
      hapticResult('success');
    } catch {
      setError('Не получилось удалить. Попробуй ещё раз.');
    }
  };

  const ready = twin.status === 'ready';
  const need = Math.max(0, twin.minPhotos - twin.okPhotos);
  const bad = twin.photos.filter((p) => !p.ok);
  const title = ready ? 'Двойник готов' : twin.status === 'liveness' ? 'Осталась проверка' : 'Создай двойника';
  const text = ready
    ? `Собран из ${twin.okPhotos} фото. Подставляется во все тренды и в ручной режим сам.`
    : twin.status === 'liveness'
      ? 'Фото подходят. Повернём голову перед камерой — так мы убедимся, что это живой ты.'
      : `Загрузи ${twin.minPhotos}–${twin.maxPhotos} своих фото с разных ракурсов — и больше ничего не придётся грузить.`;

  return (
    <div className="twin">
      <header className="twin__header">
        <h1 className="page-title">Двойник</h1>
      </header>

      {!twin.available ? <p className="twin__msg">Двойник ещё подключается — загляни чуть позже.</p> : null}

      <section className="glass twin__hero">
        <div className="twin__avatar">
          <span className="twin__avatar-ring" />
          {ready ? (
            <span className="twin__avatar-ok">
              <TickIcon />
            </span>
          ) : null}
        </div>
        <span className="twin__title">{title}</span>
        <span className="twin__text">{text}</span>
        {twin.status === 'liveness' ? (
          <button type="button" className="btn-accent twin__cta" onClick={onLiveness}>
            Пройти проверку · 3 снимка
          </button>
        ) : null}
      </section>

      <div className="twin__grid">
        {twin.photos
          .filter((p) => p.ok)
          .map((p) => (
            <TwinThumb key={p.id} id={p.id} ok onRemove={() => void remove(p.id)} />
          ))}
        {twin.okPhotos < twin.maxPhotos ? (
          <button
            type="button"
            className="twin-thumb twin-thumb--add"
            aria-label="Добавить фото"
            disabled={Boolean(uploading) || !twin.available}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? <span className="spinner spinner--sm" /> : <Plus />}
          </button>
        ) : null}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => void addFiles(e.target.files)} />
      </div>
      <p className="twin__count">
        {uploading
          ? `Проверяем фото: ${uploading.done} из ${uploading.total}…`
          : need > 0
            ? `${twin.okPhotos} из ${twin.minPhotos} фото · ещё ${need} — разные ракурсы, хороший свет, без очков`
            : `${twin.okPhotos} из ${twin.maxPhotos} фото · больше ракурсов — точнее сходство${twin.consistency !== null ? ` · сходство ${twin.consistency}%` : ''}`}
      </p>
      {bad.length ? (
        <div className="glass-list twin__bad">
          <span className="glass-list__caption">Не подошли</span>
          {bad.slice(0, 5).map((p) => (
            <div key={p.id} className="glass-list__row twin__bad-row">
              <span className="twin__bad-reason">{p.reason}</span>
              <button type="button" className="link-btn" onClick={() => void remove(p.id)}>
                Убрать
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className="error-text twin__msg" role="alert">
          {error}
        </p>
      ) : null}

      <div className="glass-list twin__list">
        <div className="glass-list__row">
          <span className="glass-list__icon" style={{ background: '#FF375F' }}>
            <MicIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Голос</span>
            <span className="trend__face-sub">Для говорящих видео и кружочков · скоро</span>
          </span>
        </div>
        <div className="glass-list__sep" />
        <div className="glass-list__row">
          <span className="glass-list__icon" style={{ background: '#0A84FF' }}>
            <DuoIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Кому можно моё лицо</span>
            <span className="trend__face-sub">Дуо с друзьями · скоро</span>
          </span>
          <ChevronDim />
        </div>
        <div className="glass-list__sep" />
        <div className="glass-list__row">
          <span className="glass-list__icon" style={{ background: '#8E8E93' }}>
            <LockIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Приватность</span>
            <span className="trend__face-sub">Фото зашифрованы, только для тебя</span>
          </span>
        </div>
      </div>

      {twin.status !== 'none' ? (
        confirmDelete ? (
          <div className="twin__confirm">
            <span>Удалить двойника и все фото? Это навсегда.</span>
            <div className="twin__confirm-btns">
              <button type="button" className="btn-glass" onClick={() => setConfirmDelete(false)}>
                Отмена
              </button>
              <button type="button" className="twin__delete" onClick={() => void del()}>
                Удалить
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="twin__delete" onClick={() => setConfirmDelete(true)}>
            Удалить двойника
          </button>
        )
      ) : null}
    </div>
  );
}
